import { useState } from "react";
import { Helmet } from "react-helmet";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

import { Link } from "react-router-dom";
import { CheckCircle, Banknote, GraduationCap, Smartphone, Shield, Calendar, Users, ArrowRight } from "lucide-react";

const Products = () => {
  const [activeTab, setActiveTab] = useState("normal");

  // Quick navigation links
  const quickLinks = [
    { id: "savings", label: "Savings Products" },
    { id: "loans", label: "Loan Products" },
    { id: "apply", label: "How to Apply" }
  ];

  const scrollToSection = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Products & Services | Natcon Sacco</title>
        <meta name="description" content="Explore Natcon Sacco's comprehensive range of savings and loan products. Emergency loans, education loans, development loans, and more. Apply online today!" />
        <meta name="keywords" content="Natcon Sacco products, savings account Kenya, loans Kenya, emergency loan, education loan, development loan, Sacco services" />
      </Helmet>
      
      <Header />
      
      <main className="pt-20">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-primary/5 via-background to-primary/10 py-20">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-lg sm:text-xl md:text-2xl lg:text-3xl xl:text-4xl 2xl:text-5xl font-bold mb-6 text-foreground leading-tight">
              Our Financial Solutions: Designed for Your Growth
            </h1>
            <h2 className="text-xl md:text-2xl text-muted-foreground mb-8 max-w-4xl mx-auto">
              Whether you're saving for the future, investing in your goals, or need support for life's unexpected moments, our products are built with you in mind.
            </h2>
            <p className="text-lg text-muted-foreground max-w-4xl mx-auto mb-12">
              At Natcon Sacco, we understand that every member has unique financial goals. That's why we've developed a focused suite of savings and loan products designed to be affordable, accessible, and flexible. Explore our solutions below and discover how we can partner with you on your journey to financial prosperity.
            </p>

            {/* Quick Navigation */}
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              {quickLinks.map((link) => (
                <Button
                  key={link.id}
                  variant="outline"
                  onClick={() => scrollToSection(link.id)}
                  className="flex items-center gap-2"
                >
                  {link.label}
                  <ArrowRight className="w-4 h-4" />
                </Button>
              ))}
            </div>
          </div>
        </section>

        {/* Savings Products Section */}
        <section id="savings" className="py-20 bg-background">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-foreground">
                Build Your Foundation: Our Savings Solutions
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
              {/* Member Deposits */}
              <Card className="h-full">
                <CardHeader className="text-center pb-4">
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Banknote className="w-8 h-8 text-primary" />
                  </div>
                  <CardTitle className="text-2xl">Member Deposits</CardTitle>
                </CardHeader>
                <CardContent className="text-center">
                  <CardDescription className="text-base mb-6">
                    This is your primary, non-withdrawable savings account that forms the foundation of your Sacco membership. Your consistent monthly deposits not only build your savings but also determine your loan eligibility and earn you competitive annual interest.
                  </CardDescription>
                  <Button asChild>
                    <Link to="/membership">Learn About Membership</Link>
                  </Button>
                </CardContent>
              </Card>

              {/* Share Capital */}
              <Card className="h-full">
                <CardHeader className="text-center pb-4">
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Users className="w-8 h-8 text-primary" />
                  </div>
                  <CardTitle className="text-2xl">Share Capital: Become an Owner</CardTitle>
                </CardHeader>
                <CardContent className="text-center">
                  <CardDescription className="text-base mb-6">
                    Your Share Capital is your ownership stake in Natcon Sacco. It's a non-refundable investment that makes you a part-owner of our society, giving you voting rights and earning you annual dividends based on the Sacco's performance.
                  </CardDescription>
                  <Button variant="outline" asChild>
                    <Link to="/about">View Our Performance</Link>
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        <Separator />

        {/* Loan Products Section */}
        <section id="loans" className="py-20 bg-background">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-foreground">
                Achieve Your Goals: Our Loan Products
              </h2>
            </div>

            <div className="max-w-6xl mx-auto">
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="grid w-full grid-cols-2 md:grid-cols-4 mb-8">
                  <TabsTrigger value="normal">Normal Loan</TabsTrigger>
                  <TabsTrigger value="emergency">Emergency Loan</TabsTrigger>
                  <TabsTrigger value="education">Education Loan</TabsTrigger>
                  <TabsTrigger value="device">Salary Advance</TabsTrigger>
                </TabsList>

                {/* Normal Loan Tab */}
                <TabsContent value="normal" className="space-y-6">
                  <Card>
                    <CardHeader className="text-center">
                      <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Banknote className="w-8 h-8 text-primary" />
                      </div>
                      <CardTitle className="text-2xl">Normal (Development) Loan</CardTitle>
                      <Badge variant="secondary" className="w-fit mx-auto">For your long-term development goals</Badge>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-base mb-6 text-center">
                        This is our primary loan product, designed to fund your significant projects like land purchase, business investment, or home improvement.
                      </CardDescription>
                      
                      <div className="grid md:grid-cols-2 gap-6">
                        <div className="space-y-4">
                          <h4 className="font-semibold text-lg">Key Features:</h4>
                          <ul className="space-y-3">
                            <li className="flex items-start gap-3">
                              <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                              <span><strong>Loan Limit:</strong> Up to 3 times your member savings</span>
                            </li>
                            <li className="flex items-start gap-3">
                              <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                              <span><strong>Repayment Period:</strong> Up to 96 months</span>
                            </li>
                            <li className="flex items-start gap-3">
                              <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                              <span><strong>Interest Rate:</strong> 1% per month (12% p.a.) on reducing balance</span>
                            </li>
                          </ul>
                        </div>
                        <div className="space-y-4">
                          <h4 className="font-semibold text-lg">Requirements:</h4>
                          <ul className="space-y-3">
                            <li className="flex items-start gap-3">
                              <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                              <span><strong>Security:</strong> Minimum of 3 guarantors required</span>
                            </li>
                            <li className="flex items-start gap-3">
                              <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                              <span><strong>Application:</strong> Apply by 10th of month for 5-day disbursement</span>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Emergency Loan Tab */}
                <TabsContent value="emergency" className="space-y-6">
                  <Card>
                    <CardHeader className="text-center">
                      <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Shield className="w-8 h-8 text-destructive" />
                      </div>
                      <CardTitle className="text-2xl">Emergency Loan</CardTitle>
                      <Badge variant="destructive" className="w-fit mx-auto">For life's unexpected moments, we're here for you</Badge>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-base mb-6 text-center">
                        When unforeseen circumstances arise, such as sudden hospitalization or funeral expenses, this loan provides fast access to funds.
                      </CardDescription>
                      
                      <div className="grid md:grid-cols-2 gap-6">
                        <div className="space-y-4">
                          <h4 className="font-semibold text-lg">Key Features:</h4>
                          <ul className="space-y-3">
                            <li className="flex items-start gap-3">
                              <CheckCircle className="w-5 h-5 text-destructive mt-0.5 flex-shrink-0" />
                              <span><strong>Loan Limit:</strong> Up to Kshs. 100,000</span>
                            </li>
                            <li className="flex items-start gap-3">
                              <CheckCircle className="w-5 h-5 text-destructive mt-0.5 flex-shrink-0" />
                              <span><strong>Disbursement Speed:</strong> Within 24 hours (max 3 working days)</span>
                            </li>
                            <li className="flex items-start gap-3">
                              <CheckCircle className="w-5 h-5 text-destructive mt-0.5 flex-shrink-0" />
                              <span><strong>Repayment Period:</strong> Up to 12 months</span>
                            </li>
                          </ul>
                        </div>
                        <div className="space-y-4">
                          <h4 className="font-semibold text-lg">Special Benefits:</h4>
                          <ul className="space-y-3">
                            <li className="flex items-start gap-3">
                              <CheckCircle className="w-5 h-5 text-destructive mt-0.5 flex-shrink-0" />
                              <span><strong>Interest Rate:</strong> 1.5% per month (18% p.a.) on reducing balance</span>
                            </li>
                            <li className="flex items-start gap-3">
                              <CheckCircle className="w-5 h-5 text-destructive mt-0.5 flex-shrink-0" />
                              <span><strong>Special Condition:</strong> Apply even with existing loan (subject to 2/3 rule)</span>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Education Loan Tab */}
                <TabsContent value="education" className="space-y-6">
                  <Card>
                    <CardHeader className="text-center">
                      <div className="w-16 h-16 bg-blue-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <GraduationCap className="w-8 h-8 text-blue-500" />
                      </div>
                      <CardTitle className="text-2xl">Education Loan</CardTitle>
                      <Badge variant="outline" className="w-fit mx-auto border-blue-500 text-blue-500">Invest in the brightest future for you and your family</Badge>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-base mb-6 text-center">
                        Facilitate the payment of school, college, university, or professional fees with our flexible education loan. Note: Only one education loan may be granted per member in a calendar year.
                      </CardDescription>
                      
                      <div className="space-y-6">
                        <div>
                          <h4 className="font-semibold text-lg mb-4">Two Loan Tiers:</h4>
                          <div className="grid md:grid-cols-2 gap-4">
                            <Card className="border-blue-200">
                              <CardHeader className="pb-3">
                                <CardTitle className="text-lg">Primary/Secondary</CardTitle>
                              </CardHeader>
                              <CardContent>
                                <Badge className="bg-blue-100 text-blue-800">Max: Kshs. 100,000</Badge>
                              </CardContent>
                            </Card>
                            <Card className="border-blue-200">
                              <CardHeader className="pb-3">
                                <CardTitle className="text-lg">College/University</CardTitle>
                              </CardHeader>
                              <CardContent>
                                <Badge className="bg-blue-100 text-blue-800">Max: Kshs. 300,000</Badge>
                              </CardContent>
                            </Card>
                          </div>
                        </div>
                        
                        <div className="grid md:grid-cols-2 gap-6">
                          <ul className="space-y-3">
                            <li className="flex items-start gap-3">
                              <CheckCircle className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                              <span><strong>Loan Limit:</strong> Up to 1.5x savings or tier max (whichever is less)</span>
                            </li>
                            <li className="flex items-start gap-3">
                              <CheckCircle className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                              <span><strong>Repayment Period:</strong> Up to 12 months</span>
                            </li>
                          </ul>
                          <ul className="space-y-3">
                            <li className="flex items-start gap-3">
                              <CheckCircle className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                              <span><strong>Interest Rate:</strong> 1.25% per month (15% p.a.) on reducing balance</span>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Device Loan Tab */}
                <TabsContent value="device" className="space-y-6">
                  <Card>
                    <CardHeader className="text-center">
                      <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Smartphone className="w-8 h-8 text-green-500" />
                      </div>
                       <CardTitle className="text-2xl">Salary Advance</CardTitle>
                       <Badge variant="outline" className="w-fit mx-auto border-green-500 text-green-500">Quick access to your upcoming salary</Badge>
                     </CardHeader>
                     <CardContent>
                       <CardDescription className="text-base mb-6 text-center">
                         Get an advance on your salary to handle immediate financial needs with quick processing and flexible terms.
                       </CardDescription>
                       
                       <div className="grid md:grid-cols-2 gap-6">
                         <div className="space-y-4">
                           <h4 className="font-semibold text-lg">Key Features:</h4>
                           <ul className="space-y-3">
                             <li className="flex items-start gap-3">
                               <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                               <span><strong>Loan Limit:</strong> Up to Kshs. 150,000</span>
                             </li>
                             <li className="flex items-start gap-3">
                               <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                               <span><strong>Repayment Period:</strong> Up to 3 months</span>
                             </li>
                             <li className="flex items-start gap-3">
                               <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                               <span><strong>Interest Rate:</strong> 2% per month (24% p.a.) on reducing balance</span>
                             </li>
                           </ul>
                         </div>
                         <div className="space-y-4">
                           <h4 className="font-semibold text-lg">Benefits:</h4>
                           <ul className="space-y-3">
                             <li className="flex items-start gap-3">
                               <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                               <span>Fast processing and approval</span>
                             </li>
                             <li className="flex items-start gap-3">
                               <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                               <span>No additional security required</span>
                             </li>
                             <li className="flex items-start gap-3">
                               <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                               <span>Automatic payroll deduction</span>
                             </li>
                           </ul>
                         </div>
                       </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            </div>
          </div>
        </section>

        <Separator />

        {/* Eligibility and Application Section */}
        <section id="apply" className="py-20 bg-muted/50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-foreground">
                Ready to Apply? Here's What You Need to Know
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
              {/* Eligibility Checklist */}
              <Card>
                <CardHeader>
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                    <CheckCircle className="w-8 h-8 text-primary" />
                  </div>
                  <CardTitle className="text-2xl">Loan Eligibility Checklist</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-4">
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                      <span>Must have completed at least 6 months of membership</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                      <span>Must have contributed a minimum of Kshs. 12,000 in savings</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                      <span>Must have at least 3 willing guarantors who are also Sacco members</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-muted-foreground">
                        <em>Note: You become eligible for a new loan immediately after clearing an existing one</em>
                      </span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              {/* Our Commitment */}
              <Card>
                <CardHeader>
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                    <Users className="w-8 h-8 text-primary" />
                  </div>
                  <CardTitle className="text-2xl">Our Commitment to You</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-base">
                    We are committed to a transparent and fair process. While the Sacco reserves the final right for loan approval, our team is here to guide you every step of the way. We aim to make our financial solutions accessible to every qualifying member.
                  </CardDescription>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* CTA Sections */}
        <section className="py-20 bg-background">
          <div className="container mx-auto px-4 space-y-12">
            {/* CTA for New Members */}
            <Card className="bg-gradient-to-r from-primary/5 to-primary/10 border-primary/20">
              <CardContent className="text-center py-12">
                <h3 className="text-2xl md:text-3xl font-bold mb-4 text-foreground">
                  Not a Member Yet?
                </h3>
                <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
                  Access to our affordable loan products is one of the biggest benefits of joining Natcon Sacco. Start your journey today to secure your financial future.
                </p>
                <Button size="lg" asChild>
                  <a href="/contact">Become a Member Now</a>
                </Button>
              </CardContent>
            </Card>

            {/* CTA for Existing Members */}
            <Card className="bg-gradient-to-r from-secondary/5 to-secondary/10 border-secondary/20">
              <CardContent className="text-center py-12">
                <h3 className="text-2xl md:text-3xl font-bold mb-4 text-foreground">
                  Already a Member?
                </h3>
                <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
                  Log in to our self-service portal to view your loan eligibility, use the loan calculator, and start your application.
                </p>
                <Button size="lg" variant="outline" asChild>
                  <a href="https://selfservice.natconsacco.or.ke/" target="_blank" rel="noopener noreferrer">
                    Login to Member Portal
                  </a>
                </Button>
              </CardContent>
            </Card>
          </div>
        </section>
      </main>

      <Footer />
      
    </div>
  );
};

export default Products;